import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import { BACKEND_URL } from "../lib/api";

const RealtimeContext = createContext({ subscribe: () => () => {}, connected: false });

const MAX_DELAY = 30000;

export const RealtimeProvider = ({ children }) => {
  const { user } = useAuth();
  const wsRef = useRef(null);
  const listenersRef = useRef(new Set());
  const reconnectAttemptsRef = useRef(0);
  const closingRef = useRef(false);
  const pingTimerRef = useRef(null);
  const [connected, setConnected] = useState(false);

  const subscribe = useCallback((handler) => {
    listenersRef.current.add(handler);
    return () => listenersRef.current.delete(handler);
  }, []);

  const emit = useCallback((evt) => {
    listenersRef.current.forEach((h) => {
      try { h(evt); } catch (e) { /* noop */ }
    });
  }, []);

  const connect = useCallback(() => {
    if (!user || user === false) return;
    const token = localStorage.getItem("token");
    if (!token) return;
    if (wsRef.current && wsRef.current.readyState <= 1) return;

    const wsUrl = BACKEND_URL.replace(/^http/, "ws") + `/api/ws?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    closingRef.current = false;

    ws.onopen = () => {
      reconnectAttemptsRef.current = 0;
      setConnected(true);
      // ping every 25s
      if (pingTimerRef.current) clearInterval(pingTimerRef.current);
      pingTimerRef.current = setInterval(() => {
        try { ws.readyState === 1 && ws.send("ping"); } catch (e) {}
      }, 25000);
    };

    ws.onmessage = (e) => {
      if (e.data === "pong") return;
      try {
        const msg = JSON.parse(e.data);
        emit(msg);
      } catch (err) {
        // ignore
      }
    };

    ws.onerror = () => {
      // close handler will trigger reconnect
    };

    ws.onclose = () => {
      setConnected(false);
      if (pingTimerRef.current) {
        clearInterval(pingTimerRef.current);
        pingTimerRef.current = null;
      }
      if (closingRef.current) return;
      const attempt = ++reconnectAttemptsRef.current;
      const base = Math.min(1000 * 2 ** attempt, MAX_DELAY);
      const jitter = base * (0.8 + Math.random() * 0.4); // ±20%
      setTimeout(connect, jitter);
    };
  }, [user, emit]);

  useEffect(() => {
    if (user && user !== false) {
      connect();
    }
    return () => {
      closingRef.current = true;
      if (pingTimerRef.current) clearInterval(pingTimerRef.current);
      if (wsRef.current && wsRef.current.readyState <= 1) {
        try { wsRef.current.close(); } catch (e) {}
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <RealtimeContext.Provider value={{ subscribe, connected }}>
      {children}
    </RealtimeContext.Provider>
  );
};

export const useRealtime = () => useContext(RealtimeContext);

/**
 * Convenience hook: subscribe to specific events.
 * @param {string|string[]} events - event name(s) to listen for
 * @param {(data, actorId) => void} handler
 */
export function useRealtimeEvent(events, handler) {
  const { subscribe } = useRealtime();
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const list = Array.isArray(events) ? events : [events];
    const unsub = subscribe((msg) => {
      if (list.includes(msg.event) || list.includes("*")) {
        handlerRef.current(msg.data, msg.actor_id, msg.event);
      }
    });
    return unsub;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Array.isArray(events) ? events.join(",") : events, subscribe]);
}
